/**
 * №29 (D-SS2) — SHELL IDENTITY, observable.
 *
 * 22.3's D-CH3 said "the docked panel never remounts" and proved it with a marker on
 * `window` that changes on every mount — and therefore also on every full document
 * load, since a reload builds a fresh `window`. That trick was right; what changed in
 * №29 is its SCOPE. The shell is now three zones, and "the shell never blinks" means
 * all three keep their identity: the nav, the stage element, and the work rail.
 *
 * So each zone stamps its own marker here, and the shell gate
 * (`tests/snapshots/shell-identity-29.spec.ts`) asserts every one of them is
 * unchanged across a navigation sequence. A zone that quietly starts remounting —
 * because someone keyed it by pathname, or moved it inside `<main>`, or wrapped it in
 * a conditional — fails loudly instead of costing a user their in-flight run.
 *
 * Read-only evidence: nothing in the app reads these back.
 */
import { useEffect } from "react";

export type ShellZone = "shell" | "nav" | "stage" | "rail";

declare global {
  interface Window {
    __KUVIO_SHELL_MOUNTS__?: Record<string, string>;
    /** How many times each zone has mounted this document. */
    __KUVIO_SHELL_MOUNT_COUNTS__?: Record<string, number>;
  }
}

let seq = 0;

/** Stamp a fresh identity for `zone` on mount. Changing = a remount or a reload. */
export function useShellIdentity(zone: ShellZone): void {
  useEffect(() => {
    try {
      seq += 1;
      const id = `${zone}_${seq}_${performance.now().toFixed(0)}`;
      window.__KUVIO_SHELL_MOUNTS__ = { ...(window.__KUVIO_SHELL_MOUNTS__ ?? {}), [zone]: id };
      const counts = window.__KUVIO_SHELL_MOUNT_COUNTS__ ?? {};
      window.__KUVIO_SHELL_MOUNT_COUNTS__ = { ...counts, [zone]: (counts[zone] ?? 0) + 1 };
    } catch {
      /* SSR / locked-down window */
    }
  }, [zone]);
}
